import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import CheckIcon from '@mui/icons-material/Check';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Switch,
} from '@mui/material';
import useAppColorMode from 'hooks/useAppColorMode';
import {
  APP_TOP_BAR_ACTION_HIT_PX,
  APP_TOP_BAR_ACTION_ICON_PX,
} from 'layout/appTopBarLayout';
import { usePageGuideContextOptional } from 'components/pageGuide/PageGuideContext';
import {
  showsClinicalHudRefreshSettings,
  isClinicalHud2ProductPath,
  isChartMind7ProductPath,
} from 'features/clinicalHud/marketing/clinicalHudMarketingRouting';
import { useClinicalHudRefreshSettingsOptional } from 'features/clinicalHud/app/ClinicalHudRefreshSettingsContext';
import {
  DISPLAY_MODE_PHONE,
  DISPLAY_MODE_SMART_GLASSES,
  DDX_ENGINE_LEGACY,
  DDX_ENGINE_UPGRADED,
  DDX_ENGINE_UPGRADED_PLUS,
} from 'features/clinicalHud/app/clinicalHudRefreshSettings';
import { useAuth } from 'hooks/useAuth';
import useGemini35TranscribeAppFlag from 'hooks/useGemini35TranscribeAppFlag';
import { GEMINI35_TRANSCRIBE_APP_IDS } from 'services/preferences/voicePreferencesService';
import {
  GEMINI35_TRANSCRIBE_HIPAA_HELPER,
  GEMINI35_TRANSCRIBE_SWITCH_LABEL,
} from 'services/voice/gemini35TranscribeConstants';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { isCareStartProductPath } from 'features/carestart/careStartProductRouting';
import { useCareStartTtsSettingsOptional } from 'features/carestart/CareStartTtsSettingsContext';
import { CARESTART_TTS_PROVIDER } from 'features/carestart/careStartTtsConstants';
import CareStartTtsVoiceDialog from 'features/carestart/CareStartTtsVoiceDialog';
import { isUnifiedProductChromeEnabled } from 'components/layout/unifiedProductChrome';
import { useProductSettingsSlot } from 'components/layout/ProductSettingsContext';

/**
 * Extensible user-settings entry point for the global top bar.
 * Add future role-specific settings below the appearance setting.
 */
function AppSettingsMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openaiVoiceDialogOpen, setOpenaiVoiceDialogOpen] = useState(false);
  const [liveVoiceDialogOpen, setLiveVoiceDialogOpen] = useState(false);
  const [deepgramVoiceDialogOpen, setDeepgramVoiceDialogOpen] = useState(false);
  const { pathname } = useLocation();
  const { isDark, toggle } = useAppColorMode();
  const pageGuide = usePageGuideContextOptional();
  const hudRefresh = useClinicalHudRefreshSettingsOptional();
  const careStartTts = useCareStartTtsSettingsOptional();
  const { user } = useAuth();
  const { registration: productSettings } = useProductSettingsSlot();
  const showProductSettings =
    isUnifiedProductChromeEnabled() && Boolean(productSettings?.open);
  const showHudRefresh =
    showsClinicalHudRefreshSettings(pathname) && Boolean(hudRefresh);
  const showChartMind7 = isChartMind7ProductPath(pathname);
  const showHud2Transcribe = isClinicalHud2ProductPath(pathname) || showChartMind7;
  const showCareStartTts =
    isCareStartProductPath(pathname) && Boolean(careStartTts) && Boolean(user?.uid);
  const gemini35Flag = useGemini35TranscribeAppFlag(
    showHud2Transcribe ? user?.uid : null,
    showChartMind7
      ? GEMINI35_TRANSCRIBE_APP_IDS.CHARTMIND7
      : GEMINI35_TRANSCRIBE_APP_IDS.CLINICAL_HUD2,
  );
  const open = Boolean(anchorEl);
  const smartGlassesOn =
    hudRefresh?.displayMode === DISPLAY_MODE_SMART_GLASSES;
  const chartMindDdxOn = hudRefresh?.chartMindDdx !== false;
  const ddxEngine = hudRefresh?.ddxEngine || DDX_ENGINE_LEGACY;
  const expertAdviceOn = hudRefresh?.expertAdviceEnabled === true;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const coachingAvailable =
    pageGuide?.ready && pageGuide?.platformEnabled !== false;
  const coachingOn = pageGuide?.userEnabled === true;

  const careStartProvider =
    careStartTts?.ttsProvider || CARESTART_TTS_PROVIDER.OPENAI;
  const careStartOpenaiVoice = careStartTts?.openaiVoice || 'nova';
  const careStartLiveVoice = careStartTts?.geminiLiveVoice || 'Aoede';
  const careStartDeepgramVoice = careStartTts?.deepgramVoice || 'asteria';
  const careStartBusy = Boolean(careStartTts?.loading || careStartTts?.saving);

  return (
    <>
      <IconButton
        type="button"
        aria-label="Settings"
        aria-controls={open ? 'app-settings-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="menu"
        onClick={handleOpen}
        sx={{
          width: APP_TOP_BAR_ACTION_HIT_PX,
          height: APP_TOP_BAR_ACTION_HIT_PX,
          flexShrink: 0,
          color: 'primary.main',
        }}
      >
        <SettingsOutlinedIcon sx={{ fontSize: APP_TOP_BAR_ACTION_ICON_PX }} />
      </IconButton>

      <Menu
        id="app-settings-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-label': 'User settings' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={toggle} sx={{ minWidth: 240 }}>
          <ListItemIcon>
            <DarkModeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Dark mode" />
          <Switch
            edge="end"
            checked={isDark}
            onClick={(event) => event.stopPropagation()}
            onChange={toggle}
            inputProps={{ 'aria-label': 'Toggle dark mode' }}
          />
        </MenuItem>
        {showProductSettings ? (
          <MenuItem
            onClick={() => {
              handleClose();
              productSettings.open();
            }}
            data-testid="product-settings-menu-item"
            sx={{ minWidth: 240 }}
          >
            <ListItemIcon>
              <SettingsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={productSettings.label} />
          </MenuItem>
        ) : null}
        {coachingAvailable ? (
          <MenuItem
            onClick={() => {
              void pageGuide.setUserEnabled(!coachingOn);
            }}
            data-testid="pageguide-settings-coaching"
            sx={{ minWidth: 240 }}
          >
            <ListItemIcon>
              <RecordVoiceOverOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="PageGuide coaching"
              secondary="Idle nudges and talk-back"
            />
            <Switch
              edge="end"
              checked={coachingOn}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, checked) => {
                void pageGuide.setUserEnabled(checked);
              }}
              inputProps={{ 'aria-label': 'Toggle PageGuide coaching' }}
            />
          </MenuItem>
        ) : null}
        {showCareStartTts ? (
          <>
            <ListSubheader disableSticky>CareStart TTS</ListSubheader>
            {[
              {
                id: CARESTART_TTS_PROVIDER.OPENAI,
                testId: 'carestart-tts-engine-openai',
                primary: 'OpenAI',
                secondary: 'Classic interview speech (recommended)',
              },
              {
                id: CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS,
                testId: 'carestart-tts-engine-gemini-flash',
                primary: 'Gemini Flash TTS',
                secondary: 'Preview — same voices as Live audio',
              },
              {
                id: CARESTART_TTS_PROVIDER.DEEPGRAM,
                testId: 'carestart-tts-engine-deepgram',
                primary: 'Deepgram Aura',
                secondary: 'English Aura voices',
              },
            ].map((opt) => {
              const selected = careStartProvider === opt.id;
              return (
                <MenuItem
                  key={opt.id}
                  selected={selected}
                  disabled={careStartBusy}
                  onClick={() => {
                    void careStartTts.setTtsProvider(opt.id);
                  }}
                  data-testid={opt.testId}
                  sx={{ minWidth: 280 }}
                >
                  <ListItemIcon>
                    <VolumeUpOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={opt.primary}
                    secondary={opt.secondary}
                  />
                  {selected ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : null}
                </MenuItem>
              );
            })}
            <MenuItem
              disabled={careStartBusy}
              onClick={() => {
                handleClose();
                setOpenaiVoiceDialogOpen(true);
              }}
              data-testid="carestart-tts-openai-voice"
              sx={{ minWidth: 280 }}
            >
              <ListItemIcon>
                <VolumeUpOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="OpenAI voice"
                secondary={
                  careStartOpenaiVoice.charAt(0).toUpperCase() +
                  careStartOpenaiVoice.slice(1)
                }
              />
            </MenuItem>
            <MenuItem
              disabled={careStartBusy}
              onClick={() => {
                handleClose();
                setLiveVoiceDialogOpen(true);
              }}
              data-testid="carestart-tts-live-voice"
              sx={{ minWidth: 280 }}
            >
              <ListItemIcon>
                <VolumeUpOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Live / Flash voice"
                secondary={careStartLiveVoice}
              />
            </MenuItem>
            <MenuItem
              disabled={careStartBusy}
              onClick={() => {
                handleClose();
                setDeepgramVoiceDialogOpen(true);
              }}
              data-testid="carestart-tts-deepgram-voice"
              sx={{ minWidth: 280 }}
            >
              <ListItemIcon>
                <VolumeUpOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Deepgram Aura voice"
                secondary={
                  careStartDeepgramVoice.charAt(0).toUpperCase() +
                  careStartDeepgramVoice.slice(1)
                }
              />
            </MenuItem>
          </>
        ) : null}
        {showHudRefresh ? (
          <>
            <ListSubheader disableSticky>
              ClinicalHUD update interval
            </ListSubheader>
            {hudRefresh.options.map((sec) => {
              const selected = hudRefresh.seconds === sec;
              return (
                <MenuItem
                  key={sec}
                  selected={selected}
                  onClick={() => hudRefresh.setSeconds(sec)}
                  data-testid={`clinical-hud-refresh-${sec}`}
                >
                  <ListItemText primary={`Every ${sec} seconds`} />
                  {selected ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : null}
                </MenuItem>
              );
            })}
            <ListSubheader disableSticky>
              ClinicalHUD display
            </ListSubheader>
            {showChartMind7 ? null : (
              <>
            <MenuItem
              onClick={() => {
                hudRefresh.setChartMindDdx(!chartMindDdxOn);
              }}
              data-testid="clinical-hud-chartmind-ddx"
              sx={{ minWidth: 280 }}
            >
              <ListItemIcon>
                <PsychologyOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="ChartMind DDx"
                secondary="Full transcript → ChartMind differential (default on)"
              />
              <Switch
                edge="end"
                checked={chartMindDdxOn}
                onClick={(event) => event.stopPropagation()}
                onChange={(_, checked) => {
                  hudRefresh.setChartMindDdx(checked);
                }}
                inputProps={{ 'aria-label': 'Toggle ChartMind DDx' }}
              />
            </MenuItem>
            <ListSubheader disableSticky>
              ChartMind DDx engine
            </ListSubheader>
            {[
              {
                id: DDX_ENGINE_LEGACY,
                testId: 'clinical-hud-ddx-engine-legacy',
                primary: 'Legacy',
                secondary: 'Original chartmind-diagnosis path',
              },
              {
                id: DDX_ENGINE_UPGRADED,
                testId: 'clinical-hud-ddx-engine-upgraded',
                primary: 'Upgraded DDx',
                secondary: 'Broader can’t-miss differential',
              },
              {
                id: DDX_ENGINE_UPGRADED_PLUS,
                testId: 'clinical-hud-ddx-engine-upgraded-plus',
                primary: 'Upgraded DDx Plus',
                secondary: 'Working-dx ranking, synonyms, competitor classes',
              },
            ].map((opt) => {
              const selected = ddxEngine === opt.id;
              return (
                <MenuItem
                  key={opt.id}
                  disabled={!chartMindDdxOn}
                  selected={selected}
                  onClick={() => {
                    if (!chartMindDdxOn) return;
                    hudRefresh.setDdxEngine(opt.id);
                  }}
                  data-testid={opt.testId}
                  sx={{ minWidth: 280 }}
                >
                  <ListItemIcon>
                    <AutoAwesomeOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={opt.primary}
                    secondary={
                      chartMindDdxOn ? opt.secondary : 'Requires ChartMind DDx on'
                    }
                  />
                  {selected ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : null}
                </MenuItem>
              );
            })}
              </>
            )}
            <MenuItem
              onClick={() => {
                hudRefresh.setDisplayMode(
                  smartGlassesOn ? DISPLAY_MODE_PHONE : DISPLAY_MODE_SMART_GLASSES,
                );
              }}
              data-testid="clinical-hud-smart-glasses-view"
              sx={{ minWidth: 280 }}
            >
              <ListItemIcon>
                <VisibilityOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Smart glasses view"
                secondary="Even G2 preview — not connected to glasses"
              />
              <Switch
                edge="end"
                checked={smartGlassesOn}
                onClick={(event) => event.stopPropagation()}
                onChange={(_, checked) => {
                  hudRefresh.setDisplayMode(
                    checked ? DISPLAY_MODE_SMART_GLASSES : DISPLAY_MODE_PHONE,
                  );
                }}
                inputProps={{ 'aria-label': 'Toggle smart glasses view' }}
              />
            </MenuItem>
            {showHud2Transcribe ? (
              <MenuItem
                onClick={() => {
                  hudRefresh.setExpertAdviceEnabled(!expertAdviceOn);
                }}
                data-testid={
                  showChartMind7
                    ? 'chartMind7.settings.expertAdvice'
                    : 'clinical-hud-2-settings-expert-advice'
                }
                sx={{ minWidth: 280 }}
              >
                <ListItemIcon>
                  <MedicalInformationOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Expert Advice"
                  secondary="Adds a shortcut to consult OpenEvidence.com from your ClinicalHUD2 workspace. You'll sign in with your own OpenEvidence account — we never see or store your OpenEvidence credentials."
                />
                <Switch
                  edge="end"
                  checked={expertAdviceOn}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(_, checked) => {
                    hudRefresh.setExpertAdviceEnabled(checked);
                  }}
                  inputProps={{ 'aria-label': 'Toggle Expert Advice' }}
                />
              </MenuItem>
            ) : null}
          </>
        ) : null}
        {showHud2Transcribe && user?.uid ? (
          <MenuItem
            onClick={() => {
              void gemini35Flag.setEnabled(!gemini35Flag.enabled);
            }}
            disabled={gemini35Flag.loading || gemini35Flag.saving}
            data-testid={
              showChartMind7
                ? 'chartMind7.settings.gemini35'
                : 'clinical-hud-2-settings-gemini35'
            }
            sx={{ minWidth: 280, alignItems: 'flex-start' }}
          >
            <ListItemIcon sx={{ mt: 0.5 }}>
              <GraphicEqIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={GEMINI35_TRANSCRIBE_SWITCH_LABEL}
              secondary={GEMINI35_TRANSCRIBE_HIPAA_HELPER}
            />
            <Switch
              edge="end"
              checked={gemini35Flag.enabled}
              disabled={gemini35Flag.loading || gemini35Flag.saving}
              onClick={(event) => event.stopPropagation()}
              onChange={(_, checked) => {
                void gemini35Flag.setEnabled(checked);
              }}
              inputProps={{ 'aria-label': GEMINI35_TRANSCRIBE_SWITCH_LABEL }}
            />
          </MenuItem>
        ) : null}
      </Menu>
      <CareStartTtsVoiceDialog
        open={openaiVoiceDialogOpen}
        onClose={() => setOpenaiVoiceDialogOpen(false)}
        title="OpenAI voice"
        voiceSet="openai"
        currentVoice={careStartOpenaiVoice}
        onSave={(voiceId) => careStartTts?.setOpenaiVoice(voiceId)}
      />
      <CareStartTtsVoiceDialog
        open={liveVoiceDialogOpen}
        onClose={() => setLiveVoiceDialogOpen(false)}
        title="Live / Flash voice"
        voiceSet="gemini"
        currentVoice={careStartLiveVoice}
        onSave={(voiceId) => careStartTts?.setGeminiLiveVoice(voiceId)}
      />
      <CareStartTtsVoiceDialog
        open={deepgramVoiceDialogOpen}
        onClose={() => setDeepgramVoiceDialogOpen(false)}
        title="Deepgram Aura voice"
        voiceSet="deepgram"
        currentVoice={careStartDeepgramVoice}
        onSave={(voiceId) => careStartTts?.setDeepgramVoice(voiceId)}
      />
    </>
  );
}

export default AppSettingsMenu;
